import React from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * Reusable Bonus Card Component
 * Used for promotional offers, bonuses, and special announcements.
 *
 * Composes the shadcn Card primitive so border/radius/shadow stay aligned
 * with the design system; overrides bg-card with a full-bleed background
 * image (or bg-card fallback) and adds the BonusCard text shadows.
 *
 * The `image` prop is rendered as the FULL-BLEED background of the card
 * (not a foreground/right-side icon). A dark overlay sits on top so the
 * text stays legible against any underlying art.
 */
const BonusCard = ({
  title,
  highlight,
  subtitle,
  description,
  onButtonClick,
  variant = "default",
  className = "",
  image = null,
  ...props
}) => {
  // Variant styles for different card types
  const variants = {
    default: "text-white",
    welcome: "text-white",
    daily: "text-white",
    vip: "text-white",
  };

  return (
    <Card
      className={cn(
        "relative overflow-hidden border-0 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 bg-cover bg-center bg-no-repeat bg-card h-full",
        variants[variant],
        className,
      )}
      style={image ? { backgroundImage: `url("${image}")` } : undefined}
      onClick={onButtonClick}
      {...props}
    >
      {/* Content container — full width now that there's no right-side
          foreground image competing for space. Hard-offset black shadows
          (no blur) match the AnonymousHero treatment: 2px on the title to
          carry the larger type, 1px on the smaller text. */}
      <div className="relative z-10 p-6 h-full flex flex-col min-h-[200px]">
        {subtitle && (
          <div
            className="text-xs md:text-sm font-semibold mb-2 uppercase tracking-[0.2em] text-primary"
            style={{ textShadow: "1px 1px 0 #000" }}
          >
            {subtitle}
          </div>
        )}

        <h3
          className="text-2xl md:text-3xl font-extrabold leading-tight mb-1"
          style={{ textShadow: "2px 2px 0 #000" }}
        >
          {title}
        </h3>
        {highlight && (
          <div
            className="text-sm md:text-base font-semibold mb-3"
            style={{ textShadow: "1px 1px 0 #000" }}
          >
            {highlight}
          </div>
        )}

        {description && (
          <p
            className="text-sm opacity-90 leading-relaxed"
            style={{ textShadow: "1px 1px 0 #000" }}
          >
            {description}
          </p>
        )}
      </div>
    </Card>
  );
};

export default BonusCard;
